<template>
  <Teleport to="body">
  <main class="fixed inset-0 z-[2000] flex min-h-0 flex-col overflow-hidden bg-[#0d0f13] text-white">
    <header class="shrink-0 border-b border-white/10 bg-[#15171c] px-4 py-3 sm:px-6">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex min-w-0 items-center gap-3">
          <button type="button" class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/5 text-zinc-300 transition hover:bg-white/10" aria-label="Back to events" @click="$emit('close')">
            <ArrowLeftIcon class="h-5 w-5" />
          </button>
          <div class="min-w-0">
            <div class="flex items-center gap-2">
              <span class="h-2 w-2 rounded-full" :class="isLive ? 'animate-pulse bg-red-500' : connectionPassed ? 'bg-emerald-400' : 'bg-zinc-500'" />
              <p class="text-[11px] font-bold uppercase tracking-[.14em]" :class="isLive ? 'text-red-400' : 'text-zinc-500'">{{ studioLabel }}</p>
            </div>
            <h1 class="mt-0.5 max-w-[44vw] truncate text-base font-bold sm:text-lg">{{ event?.title || 'Untitled webinar' }}</h1>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <button type="button" class="hidden rounded-xl border border-blue-400/20 bg-blue-500/10 px-4 py-2.5 text-sm font-semibold text-blue-200 transition hover:bg-blue-500/15 md:block" @click="openSetupWizard">Setup guide</button>
          <button type="button" class="hidden rounded-xl border border-white/10 px-4 py-2.5 text-sm font-semibold text-zinc-300 transition hover:bg-white/5 sm:block" @click="$emit('edit-event')">Edit details</button>
          <button type="button" class="rounded-xl px-5 py-2.5 text-sm font-bold shadow-lg transition" :class="isLive ? 'bg-red-500 hover:bg-red-600' : canStart ? 'bg-[#2478ff] hover:bg-blue-600' : 'cursor-not-allowed bg-zinc-700 text-zinc-400'" :disabled="busy || (!canStart && !isLive)" @click="toggleBroadcast">
            {{ busy ? 'Please wait…' : isLive ? 'End webinar' : 'Start webinar' }}
          </button>
        </div>
      </div>
    </header>

    <div class="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
      <section class="flex min-h-[520px] min-w-0 flex-1 flex-col overflow-y-auto bg-[radial-gradient(circle_at_50%_35%,#1b202b_0%,#101217_48%,#0d0f13_100%)]">
        <div class="mx-auto w-full max-w-[1280px] px-4 pt-4 sm:px-7">
          <div v-if="errorMessage" class="flex items-start gap-3 rounded-xl border border-red-400/20 bg-red-400/10 px-4 py-3 text-sm text-red-200">
            <ExclamationTriangleIcon class="mt-0.5 h-5 w-5 shrink-0" />
            <p>{{ errorMessage }}</p>
          </div>
          <div v-else-if="!connectionPassed" class="flex items-start gap-3 rounded-xl border border-blue-400/15 bg-blue-400/5 px-4 py-3 text-sm text-blue-100/80">
            <InformationCircleIcon class="mt-0.5 h-5 w-5 shrink-0" />
            <p>Your preview is private. Connect the media feed, then start the webinar when you are ready.</p>
          </div>
          <div v-if="allowance" class="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-violet-400/15 bg-violet-400/5 px-4 py-3 text-xs text-violet-100/80">
            <div>
              <p class="font-semibold text-violet-100">Your {{ allowance.plan?.name || 'current' }} plan Webinar allowance</p>
              <p class="mt-1">{{ webinarAllowanceText }}</p>
            </div>
            <p class="font-medium text-violet-200">This week: {{ webinarUsageText }}</p>
          </div>
        </div>

        <div class="flex flex-1 items-center justify-center px-4 py-5 sm:px-7">
          <div class="w-full max-w-[1180px]">
            <div class="studio-player relative w-full overflow-hidden rounded-2xl border border-white/10 bg-black shadow-[0_28px_90px_rgba(0,0,0,.45)]">
              <video
                v-if="source === 'browser' && cameraReady"
                ref="previewVideo"
                autoplay
                muted
                playsinline
                preload="auto"
                class="absolute inset-0 h-full min-h-[240px] w-full bg-black object-contain"
                @loadedmetadata="handlePreviewReady"
                @canplay="handlePreviewReady"
                @playing="previewPlaying = true"
              />

              <div v-if="source === 'browser' && !cameraReady" class="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-zinc-900 via-[#151821] to-zinc-950 p-8 text-center">
                <div class="flex h-20 w-20 items-center justify-center rounded-full bg-white/5 ring-1 ring-white/10"><VideoCameraIcon class="h-9 w-9 text-zinc-500" /></div>
                <p class="mt-5 text-lg font-semibold text-zinc-200">Set up your camera and microphone</p>
                <p class="mt-2 max-w-md text-sm leading-6 text-zinc-500">Platforma will ask for device permission and show a private preview here.</p>
                <button type="button" class="mt-5 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-zinc-900 hover:bg-zinc-100" :disabled="busy" @click="enableCamera">{{ busy ? 'Opening devices…' : 'Enable camera preview' }}</button>
              </div>

              <video
                v-if="source === 'obs'"
                ref="obsPreviewVideo"
                autoplay
                muted
                playsinline
                preload="auto"
                class="absolute inset-0 h-full min-h-[240px] w-full bg-black object-contain"
                @loadedmetadata="handleObsPreviewReady"
                @canplay="handleObsPreviewReady"
                @playing="obsPreviewPlaying = true"
              />

              <div v-if="source === 'obs' && !obsPreviewPlaying" class="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-zinc-900 via-[#151821] to-zinc-950 p-8 text-center">
                <div v-if="connectionPassed" class="h-10 w-10 animate-spin rounded-full border-4 border-white/20 border-t-violet-400" />
                <div v-else class="flex h-20 w-20 items-center justify-center rounded-full bg-violet-500/10 ring-1 ring-violet-400/20"><ComputerDesktopIcon class="h-9 w-9 text-violet-300" /></div>
                <p class="mt-5 text-lg font-semibold text-zinc-200">{{ connectionPassed ? 'Loading OBS preview' : 'Waiting for OBS' }}</p>
                <p class="mt-2 max-w-md text-sm leading-6 text-zinc-500">{{ connectionPassed ? 'Platforma is opening your private Studio monitor.' : 'Add the generated WHIP credentials to OBS, start streaming in OBS, then check the signal here.' }}</p>
              </div>

              <div v-if="source === 'browser' && cameraReady && !previewPlaying" class="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/65 backdrop-blur-sm">
                <div class="text-center"><span class="mx-auto block h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-blue-400" /><p class="mt-3 text-sm font-semibold text-zinc-300">Loading camera preview…</p></div>
              </div>

              <div class="absolute left-4 top-4 flex items-center gap-2">
                <span class="rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider" :class="isLive ? 'bg-red-500' : connectionPassed ? 'bg-emerald-600' : 'bg-black/60 text-zinc-300'">{{ isLive ? 'Live' : connectionPassed ? 'Connected' : 'Private preview' }}</span>
                <span v-if="isLive" class="rounded-full bg-black/60 px-3 py-1.5 text-xs font-semibold">{{ liveDuration }}</span>
                <span v-if="screenSharing" class="rounded-full bg-blue-500 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider">Sharing screen</span>
              </div>

              <div v-if="source === 'browser' && cameraReady" class="absolute inset-x-0 top-4 z-20 flex justify-center px-4">
                <div class="flex items-center gap-2 rounded-2xl border border-white/10 bg-black/30 p-2 shadow-2xl backdrop-blur-md">
                  <button type="button" class="studio-media-icon group" :class="microphoneOn ? 'studio-media-button-on' : 'studio-media-button-off'" :aria-label="microphoneOn ? 'Mute microphone' : 'Unmute microphone'" @click="toggleMicrophone">
                    <MicrophoneIcon v-if="microphoneOn" class="h-5 w-5" />
                    <SpeakerXMarkIcon v-else class="h-5 w-5" />
                    <span class="studio-control-tooltip">{{ microphoneOn ? 'Mute microphone' : 'Unmute microphone' }}</span>
                  </button>
                  <button type="button" class="studio-media-icon group" :class="cameraOn ? 'studio-media-button-on' : 'studio-media-button-off'" :aria-label="cameraOn ? 'Turn camera off' : 'Turn camera on'" @click="toggleCamera">
                    <VideoCameraIcon class="h-5 w-5" />
                    <span class="studio-control-tooltip">{{ cameraOn ? 'Turn camera off' : 'Turn camera on' }}</span>
                  </button>
                  <button type="button" class="studio-media-icon group" :class="screenSharing ? 'bg-blue-500 text-white hover:bg-blue-600' : 'studio-media-button-on'" :disabled="!screenShareSupported || busy" :aria-label="screenSharing ? 'Stop sharing screen' : 'Share screen'" @click="toggleScreenShare">
                    <ComputerDesktopIcon class="h-5 w-5" />
                    <span class="studio-control-tooltip">{{ screenSharing ? 'Stop sharing' : 'Share screen' }}</span>
                  </button>
                </div>
              </div>

              <div class="absolute bottom-4 right-4 z-20 flex items-center gap-3 rounded-xl border border-white/10 bg-black/70 px-3 py-2 text-[11px] font-semibold text-zinc-300 shadow-xl backdrop-blur-xl">
                <span class="inline-flex items-center gap-1.5"><UsersIcon class="h-4 w-4 text-zinc-400" />{{ attendeeCount }}</span>
                <span class="h-4 w-px bg-white/15" />
                <span class="inline-flex items-center gap-1.5"><span class="h-2 w-2 rounded-full" :class="isLive ? 'animate-pulse bg-emerald-400' : connectionPassed ? 'bg-blue-400' : 'bg-zinc-600'" />{{ isLive ? 'On air' : connectionPassed ? 'Ready' : 'Preview' }}</span>
                <span class="h-4 w-px bg-white/15" />
                <span class="font-mono text-zinc-400">{{ liveDuration }}</span>
              </div>
            </div>

            <div class="mt-4 flex flex-col gap-3 rounded-xl border border-white/5 bg-black/20 px-4 py-3 text-xs sm:flex-row sm:items-center sm:justify-between">
              <div class="flex min-w-0 items-center gap-3">
                <span class="h-2 w-2 shrink-0 rounded-full" :class="connectionDot" />
                <span class="font-semibold text-zinc-300">{{ connectionText }}</span>
                <span class="hidden truncate text-zinc-600 sm:block">{{ source === 'obs' ? 'OBS via WHIP' : screenSharing ? 'Screen + microphone' : 'Webcam + microphone' }}</span>
              </div>
              <span class="text-zinc-500">Members receive this same live feed once you start.</span>
            </div>
          </div>
        </div>
      </section>

      <aside class="flex h-[430px] w-full shrink-0 border-t border-white/10 bg-[#15171c] lg:h-auto lg:w-[450px] lg:border-l lg:border-t-0">
        <nav class="flex w-[78px] shrink-0 flex-col items-center gap-2 border-r border-white/10 bg-[#111318] py-4" aria-label="Studio tools">
          <button v-for="panel in studioPanels" :key="panel.id" type="button" class="group flex w-[62px] flex-col items-center gap-1.5 rounded-xl px-2 py-3 text-[10px] font-semibold transition" :class="activePanel === panel.id ? 'bg-blue-500/15 text-blue-300 ring-1 ring-blue-400/20' : 'text-zinc-500 hover:bg-white/5 hover:text-zinc-300'" @click="activePanel = panel.id">
            <component :is="panel.icon" class="h-5 w-5" />
            <span>{{ panel.label }}</span>
          </button>
        </nav>

        <div class="flex min-w-0 flex-1 flex-col overflow-hidden">
          <template v-if="activePanel === 'chat'">
            <WebinarStudioChat :event-id="eventId" :feature-instance-id="featureInstanceId" />
          </template>

          <template v-else-if="activePanel === 'attendees'">
            <div class="flex items-center justify-between border-b border-white/10 px-5 py-4">
              <div><h2 class="text-sm font-bold">Live attendees</h2><p class="mt-0.5 text-xs text-zinc-500">Members currently on this watch page</p></div>
              <span class="rounded-full bg-emerald-400/10 px-2.5 py-1 text-xs font-bold text-emerald-300">{{ attendeeCount }}</span>
            </div>
            <div class="flex items-center justify-between gap-4 border-b border-white/10 bg-black/10 px-5 py-4">
              <div>
                <p class="text-xs font-semibold text-zinc-300">Show count to members</p>
                <p class="mt-1 text-[11px] leading-4 text-zinc-500">Display the live attendee total on their watch page.</p>
              </div>
              <button
                type="button"
                role="switch"
                :aria-checked="showAttendeeCount"
                :aria-label="showAttendeeCount ? 'Hide attendee count from members' : 'Show attendee count to members'"
                class="relative h-6 w-11 shrink-0 rounded-full transition disabled:cursor-not-allowed disabled:opacity-40"
                :class="showAttendeeCount ? 'bg-blue-500' : 'bg-zinc-700'"
                :disabled="!creatorWs.isConnected || attendeeCountVisibilityBusy"
                @click="toggleAttendeeCountVisibility"
              >
                <span class="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition" :class="showAttendeeCount ? 'left-[22px]' : 'left-0.5'" />
              </button>
            </div>
            <div v-if="!creatorWs.isConnected" class="border-b border-amber-400/10 bg-amber-400/5 px-5 py-3 text-xs leading-5 text-amber-200">Reconnecting attendee updates…</div>
            <div v-if="!attendees.length" class="flex flex-1 flex-col items-center justify-center px-6 text-center">
              <div class="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10 ring-1 ring-emerald-400/15"><UsersIcon class="h-7 w-7 text-emerald-300" /></div>
              <p class="mt-4 text-sm font-semibold text-zinc-200">No members watching yet</p>
              <p class="mt-2 max-w-[260px] text-xs leading-5 text-zinc-500">Members will appear here automatically when they open this event’s Webinar watch page.</p>
            </div>
            <div v-else class="flex-1 overflow-y-auto">
              <TransitionGroup name="attendee-list" tag="div" class="divide-y divide-white/5">
                <div v-for="attendee in attendees" :key="attendee.memberId" class="flex items-center gap-3 px-5 py-4">
                  <div class="relative h-11 w-11 shrink-0">
                    <img v-if="attendee.avatarUrl" :src="attendee.avatarUrl" :alt="attendee.name" class="h-11 w-11 rounded-full object-cover ring-1 ring-white/10" />
                    <span v-else class="flex h-11 w-11 items-center justify-center rounded-full bg-blue-500/15 text-xs font-bold text-blue-200 ring-1 ring-blue-400/15">{{ attendeeInitials(attendee.name) }}</span>
                    <span class="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-[#15171c] bg-emerald-400" />
                  </div>
                  <div class="min-w-0 flex-1"><p class="truncate text-sm font-semibold text-zinc-200">{{ attendee.name }}</p><p class="mt-0.5 text-xs text-zinc-500">Watching now</p></div>
                  <button
                    type="button"
                    class="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-red-400/15 bg-red-500/5 px-2.5 py-2 text-[11px] font-semibold text-red-300 transition hover:border-red-400/30 hover:bg-red-500/10 disabled:cursor-wait disabled:opacity-50"
                    :disabled="Boolean(removingMemberId)"
                    :aria-label="`Remove ${attendee.name} from this webinar`"
                    @click="attendeePendingRemoval = attendee"
                  >
                    <UserMinusIcon class="h-4 w-4" />
                    {{ removingMemberId === attendee.memberId ? 'Removing…' : 'Remove' }}
                  </button>
                </div>
              </TransitionGroup>
            </div>
          </template>

          <template v-else-if="activePanel === 'offers'">
            <WebinarStudioOffers :event-id="eventId" />
          </template>

          <template v-else-if="activePanel === 'moderator'">
            <WebinarModeratorAccessPanel :event-id="eventId" />
          </template>

          <template v-else-if="activePanel === 'settings'">
            <div class="border-b border-white/10 px-5 py-4"><h2 class="text-sm font-bold">Studio settings</h2><p class="mt-0.5 text-xs text-zinc-500">Live Webinar chat and audience controls</p></div>
            <div class="flex-1 space-y-3 overflow-y-auto p-5">
              <div class="rounded-xl border border-white/10 bg-black/20 p-4">
                <div class="flex items-center justify-between gap-3"><div><p class="text-sm font-semibold text-zinc-200">Member chat</p><p class="mt-1 text-xs leading-5 text-zinc-500">Controlled by this event’s chat setting.</p></div><span class="rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider" :class="props.event?.enableChat === false ? 'bg-zinc-500/10 text-zinc-500' : 'bg-emerald-500/10 text-emerald-300'">{{ props.event?.enableChat === false ? 'Off' : 'On' }}</span></div>
              </div>
              <div class="rounded-xl border border-white/10 bg-black/20 p-4"><p class="text-sm font-semibold text-zinc-200">Link safety</p><p class="mt-1 text-xs leading-5 text-zinc-500">Only links posted by the creator are clickable. Member URLs remain plain text.</p></div>
              <div class="rounded-xl border border-white/10 bg-black/20 p-4"><p class="text-sm font-semibold text-zinc-200">Moderation</p><p class="mt-1 text-xs leading-5 text-zinc-500">Member messages can be removed directly from the Chat tab.</p></div>
              <div class="rounded-xl border border-blue-400/10 bg-blue-500/5 p-4 text-xs leading-5 text-zinc-500">Additional live chat settings can be added here without changing the Studio layout.</div>
            </div>
          </template>

          <template v-else-if="activePanel === 'setup'">
            <div class="border-b border-white/10 px-5 py-4"><h2 class="text-sm font-bold">Broadcast setup</h2><p class="mt-0.5 text-xs text-zinc-500">Choose and configure the live source.</p></div>
            <div class="flex-1 space-y-5 overflow-y-auto p-5">
              <div class="grid grid-cols-2 gap-2 rounded-xl bg-black/30 p-1.5">
                <button type="button" class="rounded-lg px-3 py-2.5 text-sm font-semibold transition" :class="source === 'browser' ? 'bg-white text-zinc-900 shadow' : 'text-zinc-400'" :disabled="isLive" @click="changeSource('browser')">Webcam</button>
                <button type="button" class="rounded-lg px-3 py-2.5 text-sm font-semibold transition" :class="source === 'obs' ? 'bg-white text-zinc-900 shadow' : 'text-zinc-400'" :disabled="isLive" @click="changeSource('obs')">OBS</button>
              </div>

              <div v-if="source === 'browser'" class="space-y-4">
                <label class="block"><span class="text-xs font-medium text-zinc-400">Camera</span><select v-model="selectedCamera" class="mt-1.5 block w-full rounded-lg border-white/10 bg-black/30 py-2.5 text-sm text-zinc-300" @change="enableCamera"><option value="">Default camera</option><option v-for="device in cameras" :key="device.deviceId" :value="device.deviceId">{{ device.label || 'Camera' }}</option></select></label>
                <label class="block"><span class="text-xs font-medium text-zinc-400">Microphone</span><select v-model="selectedMicrophone" class="mt-1.5 block w-full rounded-lg border-white/10 bg-black/30 py-2.5 text-sm text-zinc-300" @change="enableCamera"><option value="">Default microphone</option><option v-for="device in microphones" :key="device.deviceId" :value="device.deviceId">{{ device.label || 'Microphone' }}</option></select></label>
                <div class="rounded-xl border border-blue-400/15 bg-blue-500/5 p-4 text-xs leading-5 text-zinc-400"><strong class="text-blue-200">Screen sharing:</strong> use the control beneath the preview to show a slide deck, browser tab, or desktop. Your microphone stays live while the screen replaces the webcam feed.</div>
              </div>

              <div v-else class="space-y-3">
                <button v-if="!hasObsCredentials" type="button" class="w-full rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold hover:bg-violet-500" :disabled="busy" @click="prepareObs">Generate OBS credentials</button>
                <template v-else>
                  <CredentialField label="OBS WHIP server URL" :value="session.whipUrl" />
                  <CredentialField label="OBS bearer token" :value="publishToken" secret />
                  <div class="rounded-xl border border-violet-400/15 bg-violet-500/5 p-4 text-xs leading-5 text-zinc-300">
                    <p class="font-semibold text-violet-200">Configure OBS Studio</p>
                    <ol class="mt-2 list-decimal space-y-1 pl-4 text-zinc-400">
                      <li>Open <strong class="text-zinc-300">Settings → Stream</strong>.</li>
                      <li>Set <strong class="text-zinc-300">Service</strong> to <strong class="text-zinc-300">WHIP</strong>.</li>
                      <li>Paste the URL into <strong class="text-zinc-300">Server</strong>.</li>
                      <li>Paste the token into <strong class="text-zinc-300">Bearer Token</strong>, save, then click <strong class="text-zinc-300">Start Streaming</strong>.</li>
                    </ol>
                  </div>
                  <button type="button" class="text-xs font-semibold text-zinc-500 transition hover:text-zinc-300" :disabled="busy" @click="regenerateObsCredentials">Regenerate OBS credentials</button>
                </template>
              </div>
            </div>
          </template>

          <template v-else-if="activePanel === 'connection'">
            <div class="border-b border-white/10 px-5 py-4"><h2 class="text-sm font-bold">Connection test</h2><p class="mt-0.5 text-xs text-zinc-500">Verify the private media feed.</p></div>
            <div class="flex-1 overflow-y-auto p-5">
              <div class="space-y-3 rounded-xl bg-black/20 p-4 text-sm">
                <div class="flex justify-between"><span class="text-zinc-400">Media transport</span><span :class="connectionPassed ? 'text-emerald-300' : 'text-zinc-600'">WebRTC</span></div>
                <div class="flex justify-between"><span class="text-zinc-400">Signal</span><span :class="connectionPassed ? 'text-emerald-300' : 'text-zinc-600'">{{ connectionPassed ? 'Connected' : 'Not connected' }}</span></div>
                <div class="flex justify-between"><span class="text-zinc-400">Broadcast</span><span :class="isLive ? 'text-red-300' : 'text-zinc-600'">{{ isLive ? 'Live to members' : 'Private preview' }}</span></div>
                <div class="flex justify-between"><span class="text-zinc-400">Source</span><span class="text-zinc-300">{{ source === 'obs' ? 'OBS' : screenSharing ? 'Screen share' : 'Webcam' }}</span></div>
              </div>
              <button type="button" class="mt-5 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm font-semibold hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50" :disabled="connectionButtonDisabled" @click="runConnectionTest">{{ connectionButtonLabel }}</button>
              <p v-if="source === 'obs'" class="mt-3 text-xs leading-5 text-zinc-500">Start Streaming in OBS before checking the signal. Checking does not regenerate or invalidate your credentials.</p>
            </div>
          </template>

          <template v-else>
            <div class="border-b border-white/10 px-5 py-4"><h2 class="text-sm font-bold">Studio checklist</h2><p class="mt-0.5 text-xs text-zinc-500">Everything needed before going live.</p></div>
            <div class="flex-1 space-y-2 overflow-y-auto p-5">
              <div class="mb-4 flex items-center justify-between"><span class="text-xs font-semibold text-zinc-500">Readiness</span><span class="text-xs font-semibold" :class="readinessCount === 4 ? 'text-emerald-400' : 'text-amber-300'">{{ readinessCount }}/4 ready</span></div>
              <div v-for="item in readinessItems" :key="item.label" class="flex items-center gap-3 rounded-xl bg-black/20 p-3">
                <span class="flex h-7 w-7 items-center justify-center rounded-full" :class="item.ready ? 'bg-emerald-500/15 text-emerald-400' : 'bg-white/5 text-zinc-600'"><CheckIcon v-if="item.ready" class="h-4 w-4" /><ClockIcon v-else class="h-4 w-4" /></span>
                <div class="min-w-0"><p class="truncate text-sm font-semibold text-zinc-200">{{ item.label }}</p><p class="mt-0.5 truncate text-xs text-zinc-500">{{ item.note }}</p></div>
              </div>
              <div class="mt-5 rounded-xl border border-white/5 bg-black/20 p-4"><p class="text-xs font-semibold uppercase tracking-wider text-zinc-500">Member delivery</p><p class="mt-2 text-sm leading-5 text-zinc-400">Authenticated members receive this event-specific live feed on the Webinar watch page.</p></div>
            </div>
          </template>
        </div>
      </aside>
    </div>

    <div v-if="showSetupWizard" class="fixed inset-0 z-[2050] flex items-center justify-center bg-black/65 p-4 backdrop-blur-sm">
      <section class="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl border border-white/10 bg-[#191b21] shadow-[0_30px_100px_rgba(0,0,0,.65)]">
        <header class="flex items-start justify-between border-b border-white/10 px-6 py-5 sm:px-8">
          <div>
            <p class="text-[11px] font-bold uppercase tracking-[.16em] text-blue-400">Webinar studio setup</p>
            <h2 class="mt-1 text-xl font-bold">Let’s get your broadcast ready</h2>
            <p class="mt-1 text-sm text-zinc-500">Source, devices, then a private connection check.</p>
          </div>
          <button type="button" class="rounded-lg px-3 py-2 text-xs font-semibold text-zinc-500 hover:bg-white/5 hover:text-zinc-300" @click="closeSetupWizard">Skip for now</button>
        </header>

        <div class="border-b border-white/10 px-6 py-4 sm:px-8">
          <div class="grid grid-cols-3 gap-3">
            <div v-for="step in setupSteps" :key="step.id" class="flex items-center gap-2">
              <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold" :class="setupStep >= step.id ? 'bg-blue-500 text-white' : 'bg-white/5 text-zinc-600'">{{ setupStep > step.id ? '✓' : step.id }}</span>
              <span class="hidden text-xs font-semibold sm:block" :class="setupStep >= step.id ? 'text-zinc-200' : 'text-zinc-600'">{{ step.label }}</span>
            </div>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto px-6 py-6 sm:px-8">
          <div v-if="localError" class="mb-5 flex items-start gap-3 rounded-xl border border-red-400/20 bg-red-400/10 px-4 py-3 text-sm text-red-200">
            <ExclamationTriangleIcon class="mt-0.5 h-5 w-5 shrink-0" />
            <p>{{ localError }}</p>
          </div>

          <div v-if="setupStep === 1">
            <h3 class="text-lg font-bold">How are you broadcasting?</h3>
            <p class="mt-2 text-sm leading-6 text-zinc-500">You can change this before going live.</p>
            <div class="mt-5 grid gap-4 sm:grid-cols-2">
              <button type="button" class="rounded-2xl border p-5 text-left transition" :class="source === 'browser' ? 'border-blue-400/50 bg-blue-500/10 ring-1 ring-blue-400/20' : 'border-white/10 bg-black/15 hover:bg-white/5'" @click="selectWizardSource('browser')">
                <span class="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-500/15"><VideoCameraIcon class="h-6 w-6 text-blue-300" /></span>
                <span class="mt-4 block text-sm font-bold text-zinc-100">Webcam + screen share</span>
                <span class="mt-1 block text-xs leading-5 text-zinc-500">Use your browser camera and microphone, with an optional screen or slide-deck share.</span>
              </button>
              <button type="button" class="rounded-2xl border p-5 text-left transition" :class="source === 'obs' ? 'border-violet-400/50 bg-violet-500/10 ring-1 ring-violet-400/20' : 'border-white/10 bg-black/15 hover:bg-white/5'" @click="selectWizardSource('obs')">
                <span class="flex h-11 w-11 items-center justify-center rounded-xl bg-violet-500/15"><ComputerDesktopIcon class="h-6 w-6 text-violet-300" /></span>
                <span class="mt-4 block text-sm font-bold text-zinc-100">OBS Studio</span>
                <span class="mt-1 block text-xs leading-5 text-zinc-500">Use scenes, overlays, multiple cameras, screen capture, and other advanced OBS tools.</span>
              </button>
            </div>
          </div>

          <div v-else-if="setupStep === 2 && source === 'browser'">
            <h3 class="text-lg font-bold">Allow your camera and microphone</h3>
            <p class="mt-2 text-sm leading-6 text-zinc-500">Platforma only uses these devices for your private preview until you start the webinar.</p>

            <div v-if="!cameraReady" class="mt-5 flex flex-col items-center rounded-2xl border border-dashed border-white/15 bg-black/20 px-6 py-9 text-center">
              <span class="flex h-16 w-16 items-center justify-center rounded-full bg-blue-500/10 ring-1 ring-blue-400/20"><VideoCameraIcon class="h-8 w-8 text-blue-300" /></span>
              <p class="mt-4 text-sm font-semibold text-zinc-200">Camera permission is required</p>
              <p class="mt-2 max-w-md text-xs leading-5 text-zinc-500">Click below, then choose Allow in your browser’s camera and microphone permission prompt.</p>
              <button type="button" class="mt-5 rounded-xl bg-blue-500 px-5 py-3 text-sm font-bold text-white transition hover:bg-blue-600 disabled:cursor-wait disabled:opacity-60" :disabled="deviceBusy" @click="enableCamera">
                {{ deviceBusy ? 'Waiting for browser permission…' : 'Allow camera and microphone' }}
              </button>
            </div>

            <div v-else class="mt-5 grid gap-5 sm:grid-cols-[minmax(0,1fr)_250px]">
              <div class="overflow-hidden rounded-2xl border border-emerald-400/20 bg-black">
                <div class="relative aspect-video"><video ref="wizardPreviewVideo" autoplay muted playsinline preload="auto" class="absolute inset-0 h-full w-full object-contain" @loadedmetadata="handlePreviewReady" @canplay="handlePreviewReady" @playing="previewPlaying = true" /><span class="absolute left-3 top-3 rounded-full bg-emerald-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">Devices ready</span></div>
              </div>
              <div class="space-y-3">
                <label class="block"><span class="text-xs font-medium text-zinc-400">Camera</span><select v-model="selectedCamera" class="mt-1.5 block w-full rounded-lg border-white/10 bg-black/30 py-2.5 text-sm text-zinc-300" @change="enableCamera"><option value="">Default camera</option><option v-for="device in cameras" :key="device.deviceId" :value="device.deviceId">{{ device.label || 'Camera' }}</option></select></label>
                <label class="block"><span class="text-xs font-medium text-zinc-400">Microphone</span><select v-model="selectedMicrophone" class="mt-1.5 block w-full rounded-lg border-white/10 bg-black/30 py-2.5 text-sm text-zinc-300" @change="enableCamera"><option value="">Default microphone</option><option v-for="device in microphones" :key="device.deviceId" :value="device.deviceId">{{ device.label || 'Microphone' }}</option></select></label>
                <button type="button" class="text-xs font-semibold text-blue-300 hover:text-blue-200" @click="enableCamera">Retry device access</button>
              </div>
            </div>
          </div>

          <div v-else-if="setupStep === 2">
            <h3 class="text-lg font-bold">Connect OBS Studio</h3>
            <p class="mt-2 text-sm leading-6 text-zinc-500">Generate the event-specific WHIP credentials, then add them to OBS.</p>
            <div class="mt-5 space-y-3">
              <button v-if="!hasObsCredentials" type="button" class="w-full rounded-xl bg-violet-600 px-4 py-3 text-sm font-semibold hover:bg-violet-500" :disabled="busy" @click="prepareObs">{{ busy ? 'Generating…' : 'Generate OBS credentials' }}</button>
              <template v-else>
                <CredentialField label="OBS WHIP server URL" :value="session.whipUrl" />
                <CredentialField label="OBS bearer token" :value="publishToken" secret />
                <div class="rounded-xl border border-violet-400/15 bg-violet-500/5 p-4 text-xs leading-5 text-zinc-400">In OBS, open <strong class="text-zinc-200">Settings → Stream</strong>, select <strong class="text-zinc-200">WHIP</strong>, and paste these into Server and Bearer Token.</div>
              </template>
            </div>
          </div>

          <div v-else>
            <h3 class="text-lg font-bold">Run a private connection check</h3>
            <p class="mt-2 text-sm leading-6 text-zinc-500">This verifies the media feed without showing it to members.</p>
            <div class="mt-5 rounded-2xl border border-white/10 bg-black/20 p-5">
              <div class="flex items-center gap-4">
                <span class="flex h-12 w-12 items-center justify-center rounded-full" :class="connectionPassed ? 'bg-emerald-500/15 text-emerald-300' : 'bg-white/5 text-zinc-500'"><CheckIcon v-if="connectionPassed" class="h-6 w-6" /><WifiIcon v-else class="h-6 w-6" /></span>
                <div><p class="text-sm font-bold text-zinc-200">{{ connectionPassed ? 'Your media feed is connected' : 'Your feed is not connected yet' }}</p><p class="mt-1 text-xs text-zinc-500">{{ source === 'obs' ? 'Start Streaming in OBS before running this check.' : 'Your camera preview stays private during this check.' }}</p></div>
              </div>
              <button type="button" class="mt-5 w-full rounded-xl bg-blue-500 px-4 py-3 text-sm font-bold text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50" :disabled="connectionButtonDisabled" @click="runConnectionTest">{{ connectionButtonLabel }}</button>
            </div>
          </div>
        </div>

        <footer class="flex items-center justify-between border-t border-white/10 px-6 py-4 sm:px-8">
          <button type="button" class="rounded-xl px-4 py-2.5 text-sm font-semibold text-zinc-400 hover:bg-white/5 disabled:invisible" :disabled="setupStep === 1" @click="previousSetupStep">Back</button>
          <button v-if="setupStep < 3" type="button" class="rounded-xl bg-white px-5 py-2.5 text-sm font-bold text-zinc-900 hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-40" :disabled="!canContinueSetup" @click="nextSetupStep">Continue</button>
          <button v-else type="button" class="rounded-xl bg-white px-5 py-2.5 text-sm font-bold text-zinc-900 hover:bg-zinc-100" @click="finishSetupWizard">{{ connectionPassed ? 'Enter ready studio' : 'Enter studio' }}</button>
        </footer>
      </section>
    </div>

    <div v-if="attendeePendingRemoval" class="fixed inset-0 z-[2100] flex items-center justify-center bg-black/70 p-5 backdrop-blur-sm">
      <div class="w-full max-w-md rounded-2xl border border-white/10 bg-[#202228] p-6 shadow-2xl">
        <div class="flex h-12 w-12 items-center justify-center rounded-full bg-red-500/15"><UserMinusIcon class="h-6 w-6 text-red-400" /></div>
        <h2 class="mt-5 text-xl font-bold">Remove this attendee?</h2>
        <p class="mt-2 text-sm leading-6 text-zinc-400"><strong class="text-zinc-200">{{ attendeePendingRemoval.name }}</strong> will be removed immediately and will no longer see this Webinar on their registration page.</p>
        <div class="mt-6 flex justify-end gap-3">
          <button type="button" class="rounded-xl px-4 py-2.5 text-sm font-semibold text-zinc-300 hover:bg-white/5" @click="attendeePendingRemoval = null">Cancel</button>
          <button type="button" class="rounded-xl bg-red-500 px-4 py-2.5 text-sm font-semibold hover:bg-red-600" @click="confirmRemoveAttendee">Remove attendee</button>
        </div>
      </div>
    </div>

    <div v-if="showEndConfirm" class="fixed inset-0 z-[2100] flex items-center justify-center bg-black/70 p-5 backdrop-blur-sm">
      <div class="w-full max-w-md rounded-2xl border border-white/10 bg-[#202228] p-6 shadow-2xl">
        <div class="flex h-12 w-12 items-center justify-center rounded-full bg-red-500/15"><StopIcon class="h-6 w-6 text-red-400" /></div>
        <h2 class="mt-5 text-xl font-bold">End this webinar?</h2>
        <p class="mt-2 text-sm leading-6 text-zinc-400">Members will immediately stop receiving this live session.</p>
        <div class="mt-6 flex justify-end gap-3"><button type="button" class="rounded-xl px-4 py-2.5 text-sm font-semibold text-zinc-300 hover:bg-white/5" @click="showEndConfirm = false">Keep streaming</button><button type="button" class="rounded-xl bg-red-500 px-4 py-2.5 text-sm font-semibold hover:bg-red-600" :disabled="busy" @click="endBroadcast">End webinar</button></div>
      </div>
    </div>
  </main>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
  AdjustmentsHorizontalIcon,
  ArrowLeftIcon,
  ChatBubbleLeftRightIcon,
  CheckIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  Cog6ToothIcon,
  ComputerDesktopIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  MicrophoneIcon,
  ShoppingBagIcon,
  ShieldCheckIcon,
  SpeakerXMarkIcon,
  StopIcon,
  UsersIcon,
  UserMinusIcon,
  VideoCameraIcon,
  WifiIcon,
} from '@heroicons/vue/24/outline';
import { useWebinarLiveStore } from '@/stores/webinarLiveStore';
import { useWebsocketStore } from '@/stores/websocketStore';
import { createWhipPublisher } from '@/utils/webinarWhipPublisher';
import { createWhepViewer } from '@/utils/webinarWhepViewer';
import CredentialField from '@/Components/WebinarStreamCredentialField.vue';
import WebinarStudioChat from '@/Components/WebinarStudioChat.vue';
import WebinarStudioOffers from '@/Components/WebinarStudioOffers.vue';
import WebinarModeratorAccessPanel from '@/Components/WebinarModeratorAccessPanel.vue';

const props = defineProps({ event: { type: Object, default: null }, product: { type: Object, default: null } });
const emit = defineEmits(['close', 'edit-event', 'broadcast-ended']);
const store = useWebinarLiveStore();
const creatorWs = useWebsocketStore();
const eventId = computed(() => Number(props.event?.id || props.event?.eventId || 0));
const featureInstanceId = computed(() => Number(props.event?.productId || props.event?.featureInstanceId || props.product?.id || 0));
const source = ref(props.event?.source === 'obs' ? 'obs' : 'browser');
const activePanel = ref('chat');
const previewVideo = ref(null);
const obsPreviewVideo = ref(null);
const wizardPreviewVideo = ref(null);
const localStream = ref(null);
const screenStream = ref(null);
const publisher = ref(null);
const cameras = ref([]);
const microphones = ref([]);
const selectedCamera = ref('');
const selectedMicrophone = ref('');
const cameraReady = ref(false);
const cameraOn = ref(true);
const microphoneOn = ref(true);
const previewPlaying = ref(false);
const obsPreviewPlaying = ref(false);
const screenSharing = ref(false);
const deviceBusy = ref(false);
const connectionState = ref('new');
const connectionPassed = ref(false);
const showSetupWizard = ref(true);
const setupStep = ref(1);
const showEndConfirm = ref(false);
const localError = ref('');
const elapsedSeconds = ref(0);
const attendees = ref([]);
const showAttendeeCount = ref(false);
const attendeeCountVisibilityBusy = ref(false);
const removingMemberId = ref(0);
const attendeePendingRemoval = ref(null);
let visibilityBeforeChange = false;
let liveTimer = null;
let allowanceHeartbeatTimer = null;
let allowanceHeartbeatBusy = false;
let obsPollTimer = null;
let obsViewer = null;
let obsPreviewConnecting = false;
let previousBodyOverflow = '';
let unsubscribeAttendeeUpdates = null;
let unsubscribeAttendeeRemoved = null;
let unsubscribeModerationError = null;
let stopCreatorConnectionWatch = null;

const studioPanels = [
  { id: 'chat', label: 'Chat', icon: ChatBubbleLeftRightIcon },
  { id: 'settings', label: 'Settings', icon: Cog6ToothIcon },
  { id: 'attendees', label: 'Attendees', icon: UsersIcon },
  { id: 'moderator', label: 'Moderator', icon: ShieldCheckIcon },
  { id: 'offers', label: 'Offers', icon: ShoppingBagIcon },
  { id: 'setup', label: 'Setup', icon: AdjustmentsHorizontalIcon },
  { id: 'connection', label: 'Test', icon: WifiIcon },
  { id: 'checklist', label: 'Ready', icon: ClipboardDocumentCheckIcon },
];
const setupSteps = [
  { id: 1, label: 'Source' },
  { id: 2, label: 'Devices' },
  { id: 3, label: 'Connection' },
];

const session = computed(() => store.sessionForEvent(eventId.value));
const allowance = computed(() => store.allowanceForEvent(eventId.value));
const attendeeCount = computed(() => attendees.value.length);
const publishToken = computed(() => store.publishTokenForEvent(eventId.value));
const hasObsCredentials = computed(() => Boolean(session.value?.source === 'obs' && session.value?.whipUrl && publishToken.value));
const busy = computed(() => store.busy || deviceBusy.value);
const errorMessage = computed(() => localError.value || store.error);
const isLive = computed(() => session.value?.status === 'live');
const studioLabel = computed(() => isLive.value ? 'Live now' : connectionPassed.value ? 'Studio ready' : 'Webinar studio');
const canStart = computed(() => eventId.value > 0 && connectionPassed.value && session.value?.status === 'ready');
const screenShareSupported = computed(() => typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getDisplayMedia));
const connectionDot = computed(() => connectionPassed.value ? 'bg-emerald-400' : connectionState.value === 'connecting' ? 'animate-pulse bg-amber-400' : 'bg-zinc-500');
const connectionText = computed(() => connectionPassed.value ? 'Media feed connected' : connectionState.value === 'connecting' ? 'Connecting media feed' : 'Media feed not connected');
const connectionButtonDisabled = computed(() => busy.value || (source.value === 'browser' ? !cameraReady.value : !hasObsCredentials.value));
const connectionButtonLabel = computed(() => {
  if (busy.value) return source.value === 'obs' ? 'Checking signal…' : 'Connecting…';
  if (source.value === 'obs') return connectionPassed.value ? 'OBS signal connected' : 'Check OBS signal';
  return connectionPassed.value ? 'Reconnect feed' : 'Connect and test';
});
const canContinueSetup = computed(() => {
  if (setupStep.value === 1) return Boolean(source.value);
  if (setupStep.value === 2) return source.value === 'obs' ? hasObsCredentials.value : cameraReady.value;
  return true;
});
const readinessItems = computed(() => [
  { label: 'Event details', note: props.event?.dateLabel || 'Schedule ready', ready: Boolean(props.event?.title && eventId.value) },
  { label: 'Broadcast source', note: source.value === 'obs' ? 'OBS with WHIP' : screenSharing.value ? 'Screen share + microphone' : 'Browser camera and microphone', ready: Boolean(source.value) },
  { label: 'Source setup', note: source.value === 'obs' ? (hasObsCredentials.value ? 'Credentials generated' : 'Generate credentials') : (cameraReady.value ? 'Devices ready' : 'Enable camera'), ready: source.value === 'obs' ? hasObsCredentials.value : cameraReady.value },
  { label: 'Live connection', note: connectionPassed.value ? 'Feed connected privately' : 'Run connection check', ready: connectionPassed.value },
]);
const readinessCount = computed(() => readinessItems.value.filter((item) => item.ready).length);
const liveDuration = computed(() => {
  const h = Math.floor(elapsedSeconds.value / 3600);
  const m = Math.floor((elapsedSeconds.value % 3600) / 60);
  const s = elapsedSeconds.value % 60;
  return [h, m, s].map((value) => String(value).padStart(2, '0')).join(':');
});
const quotaLabel = (resource, singular, plural) => resource?.unlimited
  ? `unlimited ${plural}`
  : `${Number(resource?.publicLimit || 0)} ${Number(resource?.publicLimit || 0) === 1 ? singular : plural}`;
const webinarAllowanceText = computed(() => [
  quotaLabel(allowance.value?.streams, 'live session', 'live sessions'),
  quotaLabel(allowance.value?.minutes, 'live minute', 'live minutes'),
].join(' and '));
const webinarUsageText = computed(() => {
  const streamUsage = Number(allowance.value?.streams?.usage || 0);
  const minuteUsage = Number(allowance.value?.minutes?.usage || 0);
  return `${streamUsage} ${streamUsage === 1 ? 'session' : 'sessions'} · ${minuteUsage} ${minuteUsage === 1 ? 'minute' : 'minutes'} used`;
});

function attendeeInitials(name) {
  return String(name || 'Member')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'M';
}

function handleAttendeeUpdate(payload = {}) {
  if (Number(payload.eventId) !== eventId.value) return;
  if (featureInstanceId.value && Number(payload.featureInstanceId) !== featureInstanceId.value) return;
  attendees.value = Array.isArray(payload.attendees)
    ? payload.attendees.map((attendee) => ({
      memberId: Number(attendee.memberId || 0),
      name: String(attendee.name || 'Member'),
      avatarUrl: String(attendee.avatarUrl || ''),
      joinedAt: attendee.joinedAt || null,
    })).filter((attendee) => attendee.memberId > 0)
    : [];
  showAttendeeCount.value = payload.showAttendeeCount === true;
  attendeeCountVisibilityBusy.value = false;
}

function toggleAttendeeCountVisibility() {
  if (!creatorWs.isConnected || attendeeCountVisibilityBusy.value) return;
  visibilityBeforeChange = showAttendeeCount.value;
  showAttendeeCount.value = !showAttendeeCount.value;
  attendeeCountVisibilityBusy.value = true;
  creatorWs.send({
    type: 'creator',
    event: 'webinar:attendeeCountVisibility',
    payload: {
      eventId: eventId.value,
      featureInstanceId: featureInstanceId.value,
      showAttendeeCount: showAttendeeCount.value,
    },
    ts: Date.now(),
  });
}

function removeAttendee(attendee) {
  const memberId = Number(attendee?.memberId || 0);
  if (!memberId || !creatorWs.isConnected || removingMemberId.value) return;
  removingMemberId.value = memberId;
  creatorWs.send({
    type: 'creator',
    event: 'webinar:attendeeRemove',
    payload: {
      eventId: eventId.value,
      featureInstanceId: featureInstanceId.value,
      memberId,
    },
    ts: Date.now(),
  });
}

function confirmRemoveAttendee() {
  if (!attendeePendingRemoval.value) return;
  const attendee = attendeePendingRemoval.value;
  attendeePendingRemoval.value = null;
  removeAttendee(attendee);
}

function handleAttendeeRemoved(payload = {}) {
  if (Number(payload.eventId) !== eventId.value) return;
  if (Number(payload.memberId) === removingMemberId.value) removingMemberId.value = 0;
}

function handleModerationError(payload = {}) {
  if (Number(payload.eventId) !== eventId.value) return;
  if (payload.action === 'attendee-count-visibility') {
    showAttendeeCount.value = visibilityBeforeChange;
    attendeeCountVisibilityBusy.value = false;
  }
  if (payload.action === 'remove-attendee') removingMemberId.value = 0;
  localError.value = String(payload.message || 'Unable to update Webinar attendee controls');
}

function subscribeToAttendees() {
  if (!creatorWs.isConnected || !eventId.value) return;
  creatorWs.send({
    type: 'creator',
    event: 'webinar:attendeesSubscribe',
    payload: {
      eventId: eventId.value,
      featureInstanceId: featureInstanceId.value || undefined,
    },
    ts: Date.now(),
  });
}

function unsubscribeFromAttendees() {
  if (!creatorWs.isConnected || !eventId.value) return;
  creatorWs.send({
    type: 'creator',
    event: 'webinar:attendeesUnsubscribe',
    payload: { eventId: eventId.value },
    ts: Date.now(),
  });
}

function openSetupWizard() {
  localError.value = '';
  setupStep.value = 1;
  showSetupWizard.value = true;
}

function closeSetupWizard() {
  showSetupWizard.value = false;
  activePanel.value = source.value === 'browser' && !cameraReady.value ? 'setup' : 'chat';
}

async function selectWizardSource(value) {
  await changeSource(value);
  localError.value = '';
}

async function nextSetupStep() {
  if (!canContinueSetup.value || setupStep.value >= 3) return;
  setupStep.value += 1;
  if (setupStep.value === 2 && source.value === 'browser' && cameraReady.value) await syncPreviewStream();
}

async function previousSetupStep() {
  if (setupStep.value <= 1) return;
  setupStep.value -= 1;
  if (setupStep.value === 2 && source.value === 'browser' && cameraReady.value) await syncPreviewStream();
}

function finishSetupWizard() {
  showSetupWizard.value = false;
  activePanel.value = connectionPassed.value ? 'chat' : 'connection';
}

async function enumerateDevices() {
  const devices = await navigator.mediaDevices.enumerateDevices();
  cameras.value = devices.filter((item) => item.kind === 'videoinput');
  microphones.value = devices.filter((item) => item.kind === 'audioinput');
}

async function syncPreviewStream() {
  await nextTick();
  const stream = screenSharing.value && screenStream.value ? screenStream.value : localStream.value;
  if (!stream?.getVideoTracks?.().length) {
    previewPlaying.value = false;
    return;
  }
  const previews = [previewVideo.value, wizardPreviewVideo.value].filter(Boolean);
  await Promise.all(previews.map(async (video) => {
    video.muted = true;
    if (video.srcObject !== stream) video.srcObject = stream;
    await video.play?.().catch(() => {});
  }));
}

async function handlePreviewReady(event) {
  const video = event?.currentTarget || event?.target;
  if (!video) return;
  video.muted = true;
  await video.play?.().catch(() => {});
  if (video.readyState >= 2) previewPlaying.value = true;
}

async function handleObsPreviewReady(event) {
  const video = event?.currentTarget || event?.target;
  if (!video) return;
  video.muted = true;
  await video.play?.().catch(() => {});
  if (video.readyState >= 2) obsPreviewPlaying.value = true;
}

async function closeObsPreview() {
  const viewer = obsViewer;
  obsViewer = null;
  obsPreviewPlaying.value = false;
  if (viewer) await viewer.close().catch(() => {});
  if (obsPreviewVideo.value) obsPreviewVideo.value.srcObject = null;
}

async function connectObsPreview() {
  if (source.value !== 'obs' || obsPreviewConnecting) return;
  obsPreviewConnecting = true;
  try {
    await closeObsPreview();
    const payload = await store.fetchPreview(eventId.value);
    await nextTick();
    if (source.value !== 'obs') return;
    obsViewer = await createWhepViewer({
      video: obsPreviewVideo.value,
      whepUrl: payload.session?.whepUrl,
      token: payload.previewToken,
      iceServers: payload.session?.iceServers,
      onStateChange: (state) => {
        if (['failed', 'disconnected'].includes(state)) {
          obsPreviewPlaying.value = false;
          localError.value = 'The OBS signal is still connected, but the private Studio preview was interrupted. Run the signal check to reconnect it.';
        }
      },
    });
    localError.value = '';
  } finally {
    obsPreviewConnecting = false;
  }
}

function broadcastStream() {
  const videoTrack = screenSharing.value
    ? screenStream.value?.getVideoTracks?.()[0]
    : localStream.value?.getVideoTracks?.()[0];
  const audioTracks = localStream.value?.getAudioTracks?.() || [];
  return new MediaStream([...(videoTrack ? [videoTrack] : []), ...audioTracks]);
}

async function enableCamera() {
  localError.value = '';
  previewPlaying.value = false;
  if (!navigator.mediaDevices?.getUserMedia) {
    localError.value = 'Camera access is unavailable. Use a current browser and open Platforma over HTTPS or localhost.';
    return;
  }
  deviceBusy.value = true;
  try {
    if (publisher.value) await closePublisher();
    if (screenSharing.value) await stopScreenShare();
    stopLocalStream();
    const videoConstraints = {
      width: { ideal: 1920, max: 1920 },
      height: { ideal: 1080, max: 1080 },
      frameRate: { ideal: 30, max: 30 },
      aspectRatio: { ideal: 16 / 9 },
      ...(selectedCamera.value ? { deviceId: { exact: selectedCamera.value } } : {}),
    };
    const audioConstraints = selectedMicrophone.value
      ? { deviceId: { exact: selectedMicrophone.value }, echoCancellation: true, noiseSuppression: true }
      : { echoCancellation: true, noiseSuppression: true };
    localStream.value = await navigator.mediaDevices.getUserMedia({ video: videoConstraints, audio: audioConstraints });
    const videoTracks = localStream.value.getVideoTracks();
    if (!videoTracks.length) throw new Error('The browser opened the devices but did not return a camera video track');
    videoTracks.forEach((track) => {
      track.contentHint = 'detail';
      track.addEventListener('unmute', () => { syncPreviewStream().catch(() => {}); }, { once: true });
    });
    cameraReady.value = true;
    cameraOn.value = true;
    microphoneOn.value = true;
    await syncPreviewStream();
    await enumerateDevices();
  } catch (error) {
    stopLocalStream();
    cameraReady.value = false;
    if (error?.name === 'NotAllowedError' || error?.name === 'SecurityError') {
      localError.value = 'Camera and microphone permission was blocked. Allow both devices for this site in your browser settings, then retry.';
    } else if (error?.name === 'NotReadableError') {
      localError.value = 'The camera or microphone is already in use by another application. Close that application, then retry.';
    } else if (error?.name === 'NotFoundError') {
      localError.value = 'No available camera or microphone was found. Connect the device, then retry.';
    } else if (error?.name === 'OverconstrainedError') {
      selectedCamera.value = '';
      selectedMicrophone.value = '';
      localError.value = 'The selected device is no longer available. Device selection was reset; please retry.';
    } else {
      localError.value = error?.message || 'Unable to open camera and microphone';
    }
  } finally {
    deviceBusy.value = false;
  }
}

function stopLocalStream() {
  localStream.value?.getTracks?.().forEach((track) => track.stop());
  localStream.value = null;
  previewPlaying.value = false;
}

function toggleCamera() {
  cameraOn.value = !cameraOn.value;
  localStream.value?.getVideoTracks?.().forEach((track) => { track.enabled = cameraOn.value; });
}

function toggleMicrophone() {
  microphoneOn.value = !microphoneOn.value;
  localStream.value?.getAudioTracks?.().forEach((track) => { track.enabled = microphoneOn.value; });
}

async function startScreenShare() {
  localError.value = '';
  if (!screenShareSupported.value) {
    localError.value = 'Screen sharing is not supported by this browser.';
    return;
  }
  try {
    const display = await navigator.mediaDevices.getDisplayMedia({
      video: { frameRate: { ideal: 30, max: 30 } },
      audio: false,
    });
    const screenTrack = display.getVideoTracks()[0];
    if (!screenTrack) throw new Error('No screen video track was selected');
    screenTrack.contentHint = 'detail';
    screenStream.value = display;
    screenSharing.value = true;
    screenTrack.onended = () => { stopScreenShare(true).catch(() => {}); };
    if (publisher.value?.replaceVideoTrack) await publisher.value.replaceVideoTrack(screenTrack);
    await syncPreviewStream();
  } catch (error) {
    if (error?.name !== 'NotAllowedError') localError.value = error?.message || 'Unable to start screen sharing';
  }
}

async function stopScreenShare(fromBrowser = false) {
  const currentScreenStream = screenStream.value;
  if (!currentScreenStream) return;
  currentScreenStream.getTracks().forEach((track) => {
    track.onended = null;
    if (!fromBrowser || track.readyState !== 'ended') track.stop();
  });
  screenStream.value = null;
  screenSharing.value = false;
  const cameraTrack = localStream.value?.getVideoTracks?.()[0];
  if (publisher.value?.replaceVideoTrack && cameraTrack) await publisher.value.replaceVideoTrack(cameraTrack);
  await syncPreviewStream();
}

async function toggleScreenShare() {
  if (screenSharing.value) await stopScreenShare();
  else await startScreenShare();
}

async function closePublisher() {
  if (publisher.value) await publisher.value.close();
  publisher.value = null;
  connectionPassed.value = false;
  connectionState.value = 'closed';
}

async function changeSource(value) {
  if (value === source.value) return;
  await closeObsPreview();
  await closePublisher();
  if (screenSharing.value) await stopScreenShare();
  if (value === 'obs') {
    stopLocalStream();
    cameraReady.value = false;
  }
  source.value = value;
  activePanel.value = 'setup';
  localError.value = '';
}

async function prepareObs() {
  localError.value = '';
  await closeObsPreview();
  connectionPassed.value = false;
  connectionState.value = 'new';
  if (obsPollTimer) { window.clearInterval(obsPollTimer); obsPollTimer = null; }
  try { await store.prepare(eventId.value, 'obs'); } catch (error) { localError.value = error?.message || 'Unable to prepare OBS'; }
}

async function regenerateObsCredentials() {
  const shouldRegenerate = window.confirm('Generate a new OBS bearer token? The token currently saved in OBS will stop working.');
  if (!shouldRegenerate) return;
  await prepareObs();
}

async function runConnectionTest() {
  localError.value = '';
  if (!eventId.value) { localError.value = 'Save this Webinar event before opening the studio.'; return; }
  try {
    if (source.value === 'browser') {
      const payload = await store.prepare(eventId.value, source.value);
      await closePublisher();
      connectionState.value = 'connecting';
      publisher.value = await createWhipPublisher({
        stream: broadcastStream(),
        whipUrl: payload.session.whipUrl,
        token: payload.publishToken,
        iceServers: payload.session.iceServers,
        onStateChange: (state) => {
          connectionState.value = state;
          if (state === 'failed' || state === 'disconnected') connectionPassed.value = false;
        },
      });
      await store.markReady(eventId.value);
      connectionPassed.value = true;
      connectionState.value = 'connected';
    } else {
      if (!hasObsCredentials.value) { localError.value = 'Generate OBS credentials before checking the signal.'; return; }
      connectionState.value = 'connecting';
      pollObsStatus();
    }
  } catch (error) {
    connectionPassed.value = false;
    connectionState.value = 'failed';
    localError.value = error?.message || 'Unable to connect the live feed';
  }
}

function pollObsStatus() {
  if (obsPollTimer) window.clearInterval(obsPollTimer);
  let attempts = 0;
  obsPollTimer = window.setInterval(async () => {
    if (obsPreviewConnecting) return;
    attempts += 1;
    try {
      const payload = await store.fetchStatus(eventId.value);
      if (['ready', 'live'].includes(payload.session?.status)) {
        connectionPassed.value = true;
        connectionState.value = 'connected';
        await connectObsPreview();
        if (obsViewer) {
          window.clearInterval(obsPollTimer);
          obsPollTimer = null;
        }
      }
    } catch (error) {
      localError.value = connectionPassed.value
        ? (error?.message || 'OBS is connected, but its private Studio preview could not be opened yet')
        : (error?.message || 'Unable to check the OBS connection');
    }
    if (attempts >= 30 && obsPollTimer) {
      window.clearInterval(obsPollTimer);
      obsPollTimer = null;
      connectionState.value = connectionPassed.value ? 'connected' : 'failed';
      localError.value = connectionPassed.value
        ? 'OBS is connected, but the private Studio preview could not be opened. Run the signal check again to reconnect the monitor.'
        : 'No OBS signal was detected. In OBS, use Service: WHIP, paste the URL into Server and the token into Bearer Token, then click Start Streaming.';
    }
  }, 2000);
}

async function toggleBroadcast() {
  if (isLive.value) { showEndConfirm.value = true; return; }
  if (!canStart.value) return;
  localError.value = '';
  try {
    await store.start(eventId.value);
    elapsedSeconds.value = 0;
    liveTimer = window.setInterval(() => { elapsedSeconds.value += 1; }, 1000);
    startAllowanceHeartbeat();
  } catch (error) { localError.value = error?.message || 'Unable to start the Webinar'; }
}

function stopAllowanceHeartbeat() {
  if (allowanceHeartbeatTimer) window.clearInterval(allowanceHeartbeatTimer);
  allowanceHeartbeatTimer = null;
  allowanceHeartbeatBusy = false;
}

async function verifyAllowance() {
  if (!isLive.value || allowanceHeartbeatBusy) return;
  allowanceHeartbeatBusy = true;
  try {
    await store.heartbeat(eventId.value);
  } catch (error) {
    localError.value = error?.message || 'Your Webinar plan allowance could not be verified.';
    if (error?.details?.ended) {
      stopAllowanceHeartbeat();
      if (liveTimer) window.clearInterval(liveTimer);
      liveTimer = null;
      await closeObsPreview();
      await closePublisher();
      if (screenSharing.value) await stopScreenShare();
      emit('broadcast-ended', { eventId: eventId.value, endedAt: new Date().toISOString(), reason: 'plan_time_limit' });
    }
  } finally {
    allowanceHeartbeatBusy = false;
  }
}

function startAllowanceHeartbeat() {
  stopAllowanceHeartbeat();
  allowanceHeartbeatTimer = window.setInterval(() => { verifyAllowance().catch(() => {}); }, 15000);
}

async function endBroadcast() {
  localError.value = '';
  try {
    await store.end(eventId.value);
    await closeObsPreview();
    await closePublisher();
    if (screenSharing.value) await stopScreenShare();
    showEndConfirm.value = false;
    if (liveTimer) window.clearInterval(liveTimer);
    liveTimer = null;
    stopAllowanceHeartbeat();
    emit('broadcast-ended', { eventId: eventId.value, endedAt: new Date().toISOString() });
  } catch (error) { localError.value = error?.message || 'Unable to end the Webinar'; }
}

watch(
  [localStream, screenStream, cameraReady, screenSharing, source, showSetupWizard, setupStep],
  () => { syncPreviewStream().catch(() => {}); },
  { flush: 'post' },
);

onMounted(() => {
  previousBodyOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  unsubscribeAttendeeUpdates = creatorWs.subscribe('webinar:attendees', handleAttendeeUpdate);
  unsubscribeAttendeeRemoved = creatorWs.subscribe('webinar:attendeeRemoved', handleAttendeeRemoved);
  unsubscribeModerationError = creatorWs.subscribe('webinar:moderationError', handleModerationError);
  stopCreatorConnectionWatch = watch(
    () => creatorWs.isConnected,
    (connected) => {
      if (connected) subscribeToAttendees();
      else attendees.value = [];
    },
  );
  creatorWs.connect();
  if (creatorWs.isConnected) subscribeToAttendees();
  store.fetchStatus(eventId.value).then((payload) => {
    if (payload.session?.status !== 'live') return;
    const startedAt = Date.parse(payload.session?.startedAt || '');
    elapsedSeconds.value = Number.isFinite(startedAt)
      ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000))
      : 0;
    if (liveTimer) window.clearInterval(liveTimer);
    liveTimer = window.setInterval(() => { elapsedSeconds.value += 1; }, 1000);
    startAllowanceHeartbeat();
  }).catch(() => {});
});

onBeforeUnmount(async () => {
  document.body.style.overflow = previousBodyOverflow;
  unsubscribeFromAttendees();
  if (unsubscribeAttendeeUpdates) unsubscribeAttendeeUpdates();
  if (unsubscribeAttendeeRemoved) unsubscribeAttendeeRemoved();
  if (unsubscribeModerationError) unsubscribeModerationError();
  if (stopCreatorConnectionWatch) stopCreatorConnectionWatch();
  attendees.value = [];
  removingMemberId.value = 0;
  attendeePendingRemoval.value = null;
  if (liveTimer) window.clearInterval(liveTimer);
  stopAllowanceHeartbeat();
  if (obsPollTimer) window.clearInterval(obsPollTimer);
  await closeObsPreview();
  await closePublisher();
  if (screenSharing.value) await stopScreenShare();
  stopLocalStream();
});
</script>

<style scoped>
.studio-player {
  aspect-ratio: 16 / 9;
  min-height: 320px;
  max-height: calc(100vh - 205px);
}

.studio-media-icon {
  @apply relative inline-flex h-10 w-10 items-center justify-center rounded-xl text-xs font-semibold transition;
}

.studio-media-button-on {
  @apply bg-white/10 text-zinc-100 hover:bg-white/15;
}

.studio-media-button-off {
  @apply bg-red-500/20 text-red-200 hover:bg-red-500/30;
}

.studio-control-tooltip {
  @apply pointer-events-none absolute left-1/2 top-full z-30 mt-3 -translate-x-1/2 whitespace-nowrap rounded-lg border border-white/10 bg-zinc-950 px-2.5 py-1.5 text-[10px] font-semibold text-zinc-200 opacity-0 shadow-xl transition-opacity;
}

.studio-media-icon:hover .studio-control-tooltip,
.studio-media-icon:focus-visible .studio-control-tooltip {
  opacity: 1;
}

.attendee-list-enter-active,
.attendee-list-leave-active,
.attendee-list-move {
  transition: opacity 220ms ease, transform 220ms ease;
}

.attendee-list-enter-from,
.attendee-list-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
